import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { differenceInHours } from 'date-fns';
import { LeadStateV2, useSaveEventProductVerticalsMutation } from '@graphql/generated';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useDisclosure } from '@withjoy/joykit';
import { useProductRows } from './hooks/useProductRows';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useBooleanEmitter } from '@apps/admin/common/hooks/useBooleanEmitter';
import { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import { DashboardData } from '../../useDashboardData';
import { getLocalStorage } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import {
  LOCAL_STORAGE_BANNER_DISMISS,
  LOCAL_STORAGE_HAS_PRESENTED_RETARGET_DIALOG,
  LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_BANNER,
  LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_DIALOG,
  LOCAL_STORAGE_WON_BANNER_DISMISS,
  LOCAL_STORAGE_HAS_PRESENTED_PRINT_EARLY_ACCESS_DIALOG,
  LOCAL_STORAGE_PRINT_EARLY_ACCESS_DIALOG_TIMESTAMP
} from './DashboardContentV2.constants';
import { getLocalStorageFlag } from './DashboardContentV2.utils';
import { useTargetPrintEarlyAccessDialogDelay } from './PrintEarlyAccessDialog/useTargetPrintEarlyAccessDialogDelay';
import { useTravelBookingDialogState } from './hooks/useTravelBookingDialogState';
import { useEventTypeConfig } from '@shared/utils/eventType';

interface TravelState {
  hasDismissedBanner: boolean;
  hasDismissedWonBanner: boolean;
  dialogPresentedTimestamp: string;
  hasPresentedTravelDialog: boolean;
  hasPresentedWonBookingDialog: boolean;
  hasPresentedWonBookingBanner: boolean;
}

const localStorage = getLocalStorage();

export const useDashboardContentV2Controller = (eventHandle: string, { data, dataV3, error, refetch }: DashboardData, eventId: string) => {
  const { productSelector, bookingBannerDismissClicked, wonBookingBannerDismissClicked } = useDashboardTelemetry();
  const isMobile = useIsMobileScreen();
  const { delayLengthInHours: printDialogDelayInHours } = useTargetPrintEarlyAccessDialogDelay();

  const {
    hotelBlockEnabledValue,
    isBookingDialogOpen,
    isLoadingBookingDataOrWaitingAssignment,
    onBookingDialogClose,
    nonCriticalData,
    openTravelBookingDialog,
    shouldPresentTravelBookingDialog,
    travelBookingDialogState
  } = useTravelBookingDialogState(eventHandle);

  const hotelRoomBlockSubmitted = !!nonCriticalData?.eventByName?.hotelRoomBlocks?.submittedDateTime || false;
  const isLeadWon = nonCriticalData?.eventByName?.hotelRoomBlocks?.leadStateV2 === LeadStateV2.Won;
  const reservedRoomBlocks = nonCriticalData?.eventByName?.hotelRoomBlocks?.reservedRoomBlocks;
  const haveReservedRoomBlocks = reservedRoomBlocks && reservedRoomBlocks.length > 0;
  const [isLoading, setIsLoading] = useState(true);
  const [productVerticals, setProductVerticals] = useState<ProductVerticals>({});
  const [shouldRefreshDashboardPreview, refreshDashboardPreview] = useBooleanEmitter();

  const eventByNameData = data?.eventByName;
  const [saveEventProductVerticals] = useSaveEventProductVerticalsMutation();
  const productRows = useProductRows(eventByNameData?.pages ?? [], productVerticals, eventByNameData?.id, !!eventByNameData?.eventOnboarding?.shouldShowOnboardingSurvey);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const { isOpen: isWonBookingDialogOpen, onClose: onWonBookingDialogClose, onOpen: onWonBookingDialogOpen } = useDisclosure();
  const { isOpen: isProductSelectionOpen, onClose: onProductSelectionClose, onOpen: onProductSelectionOpen } = useDisclosure();
  const { isOpen: isPrintEarlyAccessDialogOpen, onClose: onPrintEarlyAccessDialogClose, onOpen: onPrintEarlyAccessDialogOpen } = useDisclosure();
  const [{ guestSiteLink, eventPassword }, setDialogData] = useState({ guestSiteLink: '', eventPassword: '' });
  const [bookingDialogSourceIsBanner, setBookingDialogSourceIsBanner] = useState(false);

  const { value: enableNewPrintExperienceValue } = useFeatureValue('enableNewPrintExperience');
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isWonBannerVisible, setIsWonBannerVisible] = useState(false);

  const onPresentTravelRetargetDialog = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_HAS_PRESENTED_RETARGET_DIALOG, 'true');
  }, []);

  const onDismissBanner = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_BANNER_DISMISS, 'true');
    setIsBannerVisible(false);
  }, []);

  const onDismissWonBanner = useCallback(() => {
    localStorage.setItem(getLocalStorageFlag(LOCAL_STORAGE_WON_BANNER_DISMISS, eventId), 'true');
    setIsWonBannerVisible(false);
  }, [eventId]);

  const handleOnWonBookingDialogClose = useCallback(() => {
    localStorage.setItem(getLocalStorageFlag(LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_DIALOG, eventId), 'true');
    onWonBookingDialogClose();
    setIsWonBannerVisible(true);
  }, [eventId, onWonBookingDialogClose]);

  const travelState = useMemo<TravelState>(() => {
    return {
      hasDismissedBanner: localStorage.getItem(LOCAL_STORAGE_BANNER_DISMISS) === 'true',
      hasDismissedWonBanner: localStorage.getItem(getLocalStorageFlag(LOCAL_STORAGE_WON_BANNER_DISMISS, eventId)) === 'true',
      ...travelBookingDialogState,
      hasPresentedWonBookingDialog: localStorage.getItem(getLocalStorageFlag(LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_DIALOG, eventId)) === 'true',
      hasPresentedWonBookingBanner: localStorage.getItem(getLocalStorageFlag(LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_BANNER, eventId)) === 'true'
    };
  }, [eventId, travelBookingDialogState]);

  const onPresentPrintDialog = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_HAS_PRESENTED_PRINT_EARLY_ACCESS_DIALOG, 'true');
    localStorage.setItem(LOCAL_STORAGE_PRINT_EARLY_ACCESS_DIALOG_TIMESTAMP, Date.now().toString());
  }, []);

  const printState = useMemo(
    () => ({
      dialogPresentedTimestamp: localStorage.getItem(LOCAL_STORAGE_PRINT_EARLY_ACCESS_DIALOG_TIMESTAMP) || '',
      hasPresentedPrintEarlyAccessDialog: localStorage.getItem(LOCAL_STORAGE_HAS_PRESENTED_PRINT_EARLY_ACCESS_DIALOG) === 'true'
    }),
    []
  );

  const hasPrintDialogTargetDelayPassed = useMemo<boolean>(() => {
    const dialogPresentedTimestamp = travelState.dialogPresentedTimestamp;
    if (!printState.hasPresentedPrintEarlyAccessDialog && dialogPresentedTimestamp) {
      const hoursSinceDialogPresented = differenceInHours(Date.now(), parseInt(dialogPresentedTimestamp));
      return hoursSinceDialogPresented >= printDialogDelayInHours;
    } else {
      return false;
    }
  }, [travelState.dialogPresentedTimestamp, printState.hasPresentedPrintEarlyAccessDialog, printDialogDelayInHours]);

  /**
   * As of 06/05/2024, hotel block is rolled out to 90% of US based users
   *
   * We want to force the print dialog to show for users that have not been exposed to the hotel block feature flag.
   */
  const shouldForcePrintDialog = enableNewPrintExperienceValue === 'on' && !printState.hasPresentedPrintEarlyAccessDialog && hotelBlockEnabledValue !== 'on';

  const shouldTriggerPrintDialog = useMemo(() => {
    const shouldSkipDialog =
      enableNewPrintExperienceValue !== 'on' ||
      printState.hasPresentedPrintEarlyAccessDialog ||
      // If hotel feature block is enabled, only show the print dialog if the minimum delay has passed since seeing the travel dialog
      hotelBlockEnabledValue !== 'on' ||
      !hasPrintDialogTargetDelayPassed;

    return shouldForcePrintDialog || !shouldSkipDialog;
  }, [shouldForcePrintDialog, hotelBlockEnabledValue, hasPrintDialogTargetDelayPassed, enableNewPrintExperienceValue, printState]);

  useEffect(() => {
    const { hasDismissedBanner, hasDismissedWonBanner, hasPresentedWonBookingDialog, hasPresentedWonBookingBanner } = travelState;

    if (!(data || dataV3) || isLoadingBookingDataOrWaitingAssignment) {
      // handles both loading and unassigned states
      return;
    }

    if (hotelBlockEnabledValue === 'on') {
      if (shouldPresentTravelBookingDialog) {
        openTravelBookingDialog();
      } else if (!hasDismissedBanner && !hotelRoomBlockSubmitted) {
        setIsBannerVisible(true);
      } else if (isLeadWon && !hasPresentedWonBookingDialog && haveReservedRoomBlocks) {
        onWonBookingDialogOpen();
      } else if (isLeadWon && !hasDismissedWonBanner && !hasPresentedWonBookingBanner && haveReservedRoomBlocks) {
        setIsWonBannerVisible(true);
      }
    }
  }, [
    onPresentPrintDialog,
    hotelBlockEnabledValue,
    data,
    dataV3,
    hotelRoomBlockSubmitted,
    isLoadingBookingDataOrWaitingAssignment,
    onPresentTravelRetargetDialog,
    openTravelBookingDialog,
    travelState,
    nonCriticalData,
    isLeadWon,
    onWonBookingDialogOpen,
    haveReservedRoomBlocks,
    shouldPresentTravelBookingDialog
  ]);

  // Potentially display Print Early Access Dialog
  useEffect(() => {
    if (shouldTriggerPrintDialog) {
      onPrintEarlyAccessDialogOpen();
      onPresentPrintDialog();
    }
  }, [onPrintEarlyAccessDialogOpen, shouldTriggerPrintDialog, onPresentPrintDialog]);

  useEffect(() => {
    if (eventByNameData) {
      if (data?.eventByName?.productVerticals) {
        setProductVerticals(data?.eventByName?.productVerticals);
      }
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventByNameData?.productVerticals]);

  const handleShareDialogOpen = (): void => {
    const eventPassword = data?.eventByName?.info?.eventPassword ?? '';
    const guestSiteLink = `${window.location.origin}/${eventHandle}`;
    setDialogData({ guestSiteLink, eventPassword });
    onOpen();
  };

  const handleBookingDialogOpenFromBanner = (): void => {
    setBookingDialogSourceIsBanner(true);
    openTravelBookingDialog(true);
  };

  const handleProductSelectionToggleClick = (): void => {
    productSelector.enter();
    onProductSelectionOpen();
  };

  const handleProductSelectionClose = (): void => {
    onProductSelectionClose();
  };

  const handleValuePropositionClick = (): void => {
    const isRegistryDisabled = eventByNameData?.productVerticals?.optOutOfRegistry ?? false;
    if (isRegistryDisabled) {
      saveEventProductVerticals({
        variables: {
          eventId: eventId,
          payload: {
            optOutOfRegistry: false
          }
        }
      });
    }
  };

  const handleDismissBannerClick = (e: React.MouseEvent): void => {
    bookingBannerDismissClicked();
    onDismissBanner();
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDismissWonBannerClick = (e: React.MouseEvent): void => {
    wonBookingBannerDismissClicked();
    onDismissWonBanner();
    e.preventDefault();
    e.stopPropagation();
  };

  const { config } = useEventTypeConfig();

  const isChecklistVisible =
    useFeatureValue('AdminDashboardChecklist', {
      skip: !data?.eventByName?.checklist?.id || !config?.adminDashboardChecklist
    }).value === 'treatment';

  const adminDashboardV3Enabled = useFeatureValue('adminDashboardV3').value === 'treatment';
  const shouldHidePopups =
    useFeatureValue('adminDashboardV3Popups', {
      skip: !adminDashboardV3Enabled
    }).value === 'treatment';

  return {
    data,
    error,
    eventByNameData,
    eventPassword,
    handleProductSelectionClose,
    handleValuePropositionClick,
    handleProductSelectionToggleClick,
    onProductSelectionOpen,
    handleShareDialogOpen,
    handleDismissBannerClick,
    handleBookingDialogOpenFromBanner,
    bookingDialogSourceIsBanner,
    isBannerVisible,
    isMobile,
    isOpen,
    isProductSelectionOpen,
    guestSiteLink,
    loading: isLoading,
    shouldRefreshDashboardPreview,
    refreshDashboardPreview,
    onClose,
    productRows,
    productVerticals,
    isBookingDialogOpen,
    onBookingDialogClose,

    // Print Early Access Dialog
    isPrintEarlyAccessDialogOpen,
    onPrintEarlyAccessDialogClose,
    onPrintEarlyAccessDialogOpen,
    isLeadWon,
    isWonBannerVisible,
    isWonBookingDialogOpen,
    handleDismissWonBannerClick,
    handleOnWonBookingDialogClose,

    isChecklistVisible,
    shouldHidePopups
  };
};
